import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { MessageSquareWarning } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { companiesQuery, reportsQuery } from "../../../api/ops";
import type { OpsReport } from "../../../api/types";
import { DataTable } from "../../../components/DataTable";
import { Card, EmptyState, ErrorState, Loading, PageHeader, Select } from "../../../components/ui";
import { fmtDateTime } from "../../../lib/format";
import { useNavigate } from "../../../lib/navigate";
import { KindBadge, reportCode, SourceBadge, StatusBadge, TranscriptBadge } from "./-badges";

const searchSchema = z.object({
  company: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/reports/")({
  staticData: { crumb: { label: "Reports", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  component: ReportsPage,
});

const col = createColumnHelper<OpsReport>();

function ReportsPage() {
  const { company } = Route.useSearch();
  const navigate = useNavigate();
  const companies = useQuery(companiesQuery);
  const q = useQuery(reportsQuery(company));
  const columns = useMemo(
    () => [
      col.accessor("number", {
        header: "Report",
        enableSorting: false,
        cell: (c) => <span className="font-mono text-caption text-ink-2">{reportCode(c.getValue())}</span>,
      }),
      col.accessor((r) => r.company.name, {
        id: "company",
        header: "Company",
        enableSorting: false,
        cell: (c) => <span className="whitespace-nowrap">{c.getValue()}</span>,
      }),
      col.accessor("created_at", {
        header: "Sent",
        enableSorting: false,
        cell: (c) => <span className="tnum whitespace-nowrap text-ink-2">{fmtDateTime(c.getValue())}</span>,
      }),
      col.accessor((r) => r.title || r.excerpt, {
        id: "problem",
        header: "Problem",
        enableSorting: false,
        cell: (c) =>
          c.getValue() ? (
            <span className="line-clamp-1 max-w-[440px]">{c.getValue()}</span>
          ) : (
            <span className="text-ink-3">No description</span>
          ),
      }),
      col.accessor("source", {
        header: "Source",
        enableSorting: false,
        cell: (c) =>
          c.getValue() === "assistant" ? (
            <span className="flex items-center gap-1.5">
              <SourceBadge severity={c.row.original.severity} kind={c.row.original.report_kind} />
              {c.row.original.report_kind && c.row.original.report_kind !== "bug" ? (
                <KindBadge kind={c.row.original.report_kind} />
              ) : null}
            </span>
          ) : (
            <TranscriptBadge status={c.row.original.transcript_status} />
          ),
      }),
      col.accessor("status", {
        header: "Status",
        enableSorting: false,
        cell: (c) => <StatusBadge status={c.getValue()} fixedVersion={c.row.original.fixed_version} />,
      }),
      col.accessor("app_version", {
        header: "Version",
        enableSorting: false,
        cell: (c) => <span className="font-mono text-caption text-ink-2">{c.getValue() ?? ""}</span>,
      }),
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Reports" />
      <div className="mb-3">
        <Select
          aria-label="Company"
          className="w-56"
          value={company ?? ""}
          onValueChange={(v) =>
            navigate({ to: "/reports", search: { company: v || undefined }, replace: true })
          }
          options={[
            { value: "", label: "All companies" },
            ...(companies.data?.items ?? []).map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
      </div>
      <Card>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <DataTable
            data={q.data.items}
            columns={columns}
            getRowId={(r) => `${r.company.id}:${r.id}`}
            onRowClick={(r) =>
              navigate({
                to: "/reports/$companyId/$reportId",
                params: { companyId: r.company.id, reportId: r.id },
              })
            }
            rowLabel={(r) =>
              `${r.company.name} ${reportCode(r.number) || "report"}, ${fmtDateTime(r.created_at)}`
            }
            empty={<EmptyState icon={<MessageSquareWarning />} title="No problem reports" />}
          />
        )}
      </Card>
    </>
  );
}
